/* ──────────────────────────────────────────────────────────────
   src/pages/reports/asignaturas/HorasAsignaturasSemanalesPage.tsx
   Informe: horas semanales por asignatura
   ────────────────────────────────────────────────────────────── */

import { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Stack,
  Button,
  TextField,
  InputAdornment,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { useHorasAsignaturaSemanalReport } from '../../../hooks/useReportJob';
import { getWeeksBetween, pivotWeeks } from '../../../components/utils/pivot';
import type { HorasAsignaturaSemanalRow } from '../../../api/reports';
import { useTranslation } from 'react-i18next';
import { downloadReportFile } from '../../../components/utils/downloadXlsx';

export default function HorasAsignaturasSemanalesPage() {
  const { t } = useTranslation<'translation'>();
  const monday = dayjs().startOf('isoWeek');
  const sunday = monday.add(6, 'day');

  const [desde, setDesde] = useState(monday.format('YYYY-MM-DD'));
  const [hasta, setHasta] = useState(sunday.format('YYYY-MM-DD'));

  const [jobId, setJobId] = useState<string>();
  const { run, useStatus } = useHorasAsignaturaSemanalReport();
  const { data: status } = useStatus(jobId);

  const [search, setSearch] = useState('');

  const pendiente = !!status && !status.done && !status.failed;
  const rows: HorasAsignaturaSemanalRow[] = status?.result?.rows ?? [];

  /* ───────── filtro + pivot ───────── */
  const { weeks, data } = useMemo(() => {
    const q = search.toLowerCase();

    const filtered = rows
      .filter((r) => r.asignatura.toLowerCase().includes(q))
      .map((r) => ({
        profesor: r.asignatura,
        weekKey: r.weekKey,
        weekLabel: r.weekLabel,
        horas: r.horas,
      }));

    const { keys, labels } = getWeeksBetween(desde, hasta);

    return pivotWeeks(filtered, keys, labels);
  }, [rows, search, desde, hasta]);

  const handleGenerate = async () => {
    const { jobId } = await run({ desde, hasta });
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error('Error al descargar:', error);
      }
    }
  };

  /* ───────── render ───────── */
  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('asigHorasSemanales.titulo')}</Typography>

      {/* barra superior */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          type="date"
          label={t('asigHorasSemanales.filtros.desde')}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          label={t('asigHorasSemanales.filtros.hasta')}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />
        <Button variant="contained" onClick={handleGenerate}>
          {t('asigHorasSemanales.filtros.generar')}
        </Button>

        {rows.length > 0 && (
          <TextField
            placeholder={t<string>('asigHorasSemanales.filtros.buscar')}
            size="small"
            sx={{ ml: 'auto', maxWidth: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Stack>

      {/* progreso */}
      {pendiente && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status!.progress} />
          <Typography variant="body2">
            {t('asigHorasSemanales.estado.generando', {
              progress: status!.progress,
            })}
          </Typography>
        </Stack>
      )}

      {/* link descarga / error */}
      {status?.done && status.result && (
        <Alert severity="success">
          {t('asigHorasSemanales.estado.exito')}{' '}
          <Button
            variant="text"
            size="small"
            onClick={handleDownload}
            sx={{ textTransform: 'none', textDecoration: 'underline', p: 0, minWidth: 'auto' }}
          >
            {t('asigHorasSemanales.estado.descargar')}
          </Button>{' '}
          {t('asigHorasSemanales.estado.filas', {
            count: status.result.registros,
          })}
        </Alert>
      )}
      {status?.failed && <Alert severity="error">{t('asigHorasSemanales.estado.error')}</Alert>}

      {/* tabla */}
      {status?.done && (
        <Paper>
          {!data.length ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress />
            </Stack>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 3,
                        bgcolor: 'background.default',
                        fontWeight: 'bold',
                      }}
                    >
                      {t('asigHorasSemanales.tabla.asignatura')}
                    </TableCell>
                    {weeks.map((w) => (
                      <TableCell
                        key={w}
                        align="right"
                        sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
                      >
                        {w}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row) => (
                    <TableRow key={row.profesor}>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          bgcolor: 'background.paper',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.profesor}
                      </TableCell>
                      {weeks.map((w) => (
                        <TableCell key={w} align="right">
                          {row[w]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Stack>
  );
}
